import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { AndroWebCoreComponent } from '@app/core/AndroWebCoreComponent';
import { Contact } from '@app/models/contact';
import { OrderOccasion } from '@app/models/order-occasion';
import { Site } from '@app/models/site';
import { User } from '@app/models/user';
import { BasketService } from '@app/api/basket.service';
import { SiteService } from '@app/api/site.service';
import { UserService } from '@app/api/user.service';

@Component({
  selector: 'app-home-page-delivery-tab',
  templateUrl: './home-page-delivery-tab.component.html'
})
export class HomePageDeliveryTabComponent extends AndroWebCoreComponent implements OnInit {
  @Output('onSelection') private _onSelection: EventEmitter<{ postcode: string, site: Site }>;
  @Output('onNoStoresFound') private _onNoStoresFound: EventEmitter<boolean>;

  public searchPerformed: boolean;
  public errorPostCode: boolean;
  public noStoresFound: boolean;
  public shouldRedirect: boolean;
  public loadingStores: boolean;
  public foundStores: Site[];
  public postcodeFormControl: FormControl<string>;

  private _enteredPostcode: string;

  constructor(
    private _userService: UserService,
    private _siteService: SiteService,
    private _basketService: BasketService
  ) {
    super();
    this._onSelection = new EventEmitter<{ postcode: string, site: Site }>();
    this._onNoStoresFound = new EventEmitter<boolean>();
    this.postcodeFormControl = new FormControl<string>('');
  }

  ngOnInit() {
    this.autoFillPostCode();
  }

  /**
  * validates if the given postcode is an actual valid postcode and if so, finds sites that can service it.
  * Additionally if the user is not logged in it'll cache the given postcode for auto-filling on
  * forms on the checkout page
  * @param postcode - the given postcode
  */
  public inputPostCode(): void {
    this.searchPerformed = false;
    const searchInput: string = this.postcodeFormControl.value.replace(/[\s]/g, '').toLowerCase();

    this.errorPostCode = !this.isValidPostalCode(searchInput);

    if (!this.isValidPostalCode(searchInput)) {
      return;
    }

    if (!this._userService.currentUser || this._userService.currentUser.Contacts?.length === 0) {
      localStorage.setItem(this._sessionPostCodeKey, searchInput);
    }

    this._enteredPostcode = searchInput;
    this.findSitesByPostCode(searchInput);
  }

  /**
  * returns the phone number with the HTML tel marker to allow it function as a clickable phone number
  * @param number - the phone number
  */
  public getPhoneNumber(number: string): string {
    return `tel:${number?.replace(/\s/g, '')}`;
  }

  /**
   * outputs the selected store and postcode.
   * @param site
   */
  public setSelectedStore(site: Site): void {
    this._onSelection.emit({ postcode: this._enteredPostcode, site });
  }

  /**
   * Auto-fills the postcode input with a logged-in Users default or first contact (address) details
   */
  private autoFillPostCode(): void {
    this.subscriptions$['HomePageDeliveryTabComponent-autoFillPostCode-userService-currentUser$']
      = this._userService.currentUser$.subscribe((user: User) => {
        const contacts = user?.Contacts.filter((x) => x.Nickname !== 'BlankAddress');

        if (contacts?.length > 0) {
          const defaultContact: Contact = contacts.find((c: Contact) => c.IsDefault);
          const postcode: string = defaultContact ? defaultContact.PostCode : contacts[0].PostCode;
          this.postcodeFormControl.setValue(postcode);
        }
      });
  }

  /**
  * Does a regex test against the postcode and returns whether it's valid or not
  * @param postCode - the postcode
  */
  private isValidPostalCode(postcode: string): boolean {
    const regex: RegExp = /\b((?:(?:gir)|(?:[a-pr-uwyz])(?:(?:[0-9](?:[a-hjkpstuw]|[0-9])?)|(?:[a-hk-y][0-9](?:[0-9]|[abehmnprv-y])?)))) ?([0-9][abd-hjlnp-uw-z]{2})\b/i;
    const postCode: string = postcode.replace(new RegExp(' ', 'g'), '').toUpperCase();
    return regex.test(postCode);
  }

  /**
  * Finds sites that what trade zones that include the input postcode
  * @param postCode - the postcode
  */
  private findSitesByPostCode(postCode: string): void {
    this.loadingStores = true;

    this.subscriptions$['HomePageDeliveryTabComponent-findSitesByPostCode-siteService-getSitesByPostCode']
      = this._siteService.getSitesByPostCode(postCode).subscribe((foundSites: Site[]) => {
        this.searchPerformed = true;
        this.loadingStores = false;
        this.updateMatchingSitesByOccasionAndPostcode(foundSites.sort((a: Site, b: Site) => a.Name.localeCompare(b.Name)), postCode);
      });
  }

  /**
   * updates the list of sites matching postcode and occasion served.
   * @param {Site[]} stores
   */
  private updateMatchingSitesByOccasionAndPostcode(stores: Site[], postCode: string): Site[] {
    const includesDelivery: (occasions: OrderOccasion[]) => boolean = (x: OrderOccasion[]) => x.includes(OrderOccasion.Delivery);
    this.foundStores = stores.filter((site: Site) => includesDelivery(site.OccasionsSupported ?? this.tenant.OccasionsSupported));

    this.noStoresFound = this.foundStores.length === 0;
    this._onNoStoresFound.emit(this.noStoresFound);

    if (this.foundStores.length === 1) {
      this.setSelectedStore(this.foundStores[0]);
    } else if (this.noStoresFound) {
      this.noFoundSitesRedirect(postCode);
    }

    return this.foundStores;
  }

  /**
  * Finds sites have trade zones within the users postcode it either shows an info message or redirects the user
  * @param postCode - the postcode
  */
  private noFoundSitesRedirect(postCode: string): void {
    const noSitesFoundRedirect: string = this._configurationService.getNoSitesForLocationUriTemplate();
    this.shouldRedirect = (noSitesFoundRedirect && noSitesFoundRedirect !== '');

    if (this.shouldRedirect) {
      location.href = noSitesFoundRedirect.replace('{postCode}', postCode);
    } else {
      this._basketService.updatePostcodeWithinDelivery(false);
    }
  }
}
